<template>
  <div>
    <!-- Apartment Filters -->
    <v-row class="mb-4">
      <v-col cols="12">
        <apartment-filters
          :filters="filters"
          :is-loading="isLoading"
          :floors-options="floorsOptions"
          :rooms-options="roomsOptions"
          @update:filters="filters = $event"
          @apply-filters="applyFilters"
          @reset-filters="resetFilters"
        ></apartment-filters>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="projectApartments"
      :items-per-page="10"
      class="elevation-1"
      single-select
      :search="search"
    >
      <template v-slot:top>
        <v-row
          class="mb-0"
          style="border-bottom: 1px solid rgba(255, 255, 255, 0.12)"
        >
          <v-col class="d-flex align-center"> </v-col>
          <v-col cols="2" sm="2" md="2">
            <v-text-field
              dense
              v-model="search"
              append-icon="mdi-magnify"
              outlined=""
              label="Search"
              hide-details
              class="mr-4"
            ></v-text-field>
          </v-col>
        </v-row>
      </template>
      <template v-slot:item.name="{ item }">
        <tr>
          {{
            item.name
          }}
        </tr>
      </template>
      <template v-slot:item.action="{ item }">
        <!-- <v-icon small class="mr-3" @click="editPois(item.id)"
          >mdi-alpha-p-circle-outline</v-icon
        > -->
        <v-select
          :items="buildings"
          placeholder="Change building masterplan/community"
          item-text="name"
          item-value="id"
          size="2"
          class="mr-1"
          @change="changeBuilding($event, item)"
          >edit</v-select
        >
      </template>
    </v-data-table>
  </div>
</template>

<script>
import Enums from '../helpers/Enums'
import ApartmentFilters from '../components/ApartmentFilters'

export default {
  name: 'BuildingSettings',
  components: {
    ApartmentFilters,
  },
  props: {
    projectApartments: {
      type: Array,
      required: true,
      default: () => []
    },
    context: {
      type: String,
      required: false,
      default: 'apartment'
    }
  },
  data: function() {
    return {
      buildings: this.$store.state.BuildingStore.projectBuildings,
      search: '',
      // Filter properties
      filters: {
        statusIds: [],
        floorMin: null,
        floorMax: null,
        rooms: [],
      },
      isLoading: false,
      // Static dropdown options (set once on initial load)
      staticFloorsOptions: [],
      staticRoomsOptions: [],
    }
  },
  computed: {
    headers() {
      this.projectApartments.forEach(
        e =>
          (e.buildingName = this.buildings.find(
            i => i.id === e.buildingId
          ).name)
      )
      return [
        {
          text: this.context.toUpperCase(),
          align: 'left',
          sortable: true,
          value: 'name'
        },
        {
          text: 'APARTMENT ID',
          align: 'left',
          sortable: true,
          value: 'id'
        },
        {
          text: 'BUILDING',
          align: 'left',
          sortable: true,
          value: 'buildingName'
        },
        {
          text: 'BUILDING ID',
          sortable: false,
          align: 'left',
          value: 'buildingId'
        },
        {
          text: 'CHANGE BUILDING',
          value: 'action',
          sortable: false,
          align: 'right'
        }
      ]
    },
    floorsOptions() {
      return this.staticFloorsOptions
    },
    roomsOptions() {
      return this.staticRoomsOptions
    },
  },
  watch: {
    '$store.state.ApartmentStore.projectApartments'() {
      return (this.projectApartments = this.$store.state.ApartmentStore.projectApartments)
    },
    // Watch for apartments changes to initialize static options
    '$store.state.ApartmentStore.projectApartments': function(newApartments) {
      if (
        newApartments &&
        newApartments.length > 0 &&
        (this.staticFloorsOptions.length === 0 || this.staticRoomsOptions.length === 0)
      ) {
        this.initializeStaticOptions()
      }
    },
  },
  methods: {
    changeBuilding(id, item) {
      this.$store.dispatch('ApartmentStore/updateApartment', {
        id: item.id,
        name: item.name,
        buildingId: id,
        apartmentTypeId: item.apartmentTypeId,
        apartmentOrientationId: item.apartmentOrientationId,
        floor: item.floor,
        area: item.area,
        rooms: item.rooms,
        statusId: item.statusId,
        properties: item.properties
      })
    },
    applyFilters: async function() {
      this.isLoading = true

      // Build query parameters object
      const queryParams = {}

      if (this.filters.statusIds && this.filters.statusIds.length > 0) {
        queryParams.statusIds = this.filters.statusIds.join(',')
      }

      // Handle floor min/max logic
      if (this.filters.floorMin !== null && this.filters.floorMin !== '') {
        queryParams.floorMin = this.filters.floorMin
      }

      if (this.filters.floorMax !== null && this.filters.floorMax !== '') {
        queryParams.floorMax = this.filters.floorMax
      }

      // If only min is set, set max to maximum available floor
      if (
        this.filters.floorMin !== null &&
        this.filters.floorMin !== '' &&
        (this.filters.floorMax === null || this.filters.floorMax === '')
      ) {
        const maxFloor = Math.max(
          ...this.floorsOptions.map(option => option.value)
        )
        queryParams.floorMax = maxFloor
      }

      // If only max is set, set min to minimum available floor
      if (
        this.filters.floorMax !== null &&
        this.filters.floorMax !== '' &&
        (this.filters.floorMin === null || this.filters.floorMin === '')
      ) {
        const minFloor = Math.min(
          ...this.floorsOptions.map(option => option.value)
        )
        queryParams.floorMin = minFloor
      }

      if (this.filters.rooms && this.filters.rooms.length > 0) {
        queryParams.rooms = this.filters.rooms.join(',')
      }

      try {
        await this.$store.dispatch(
          'ApartmentStore/fetchProjectApartments',
          queryParams
        )
      } catch (error) {
        console.error('Error applying filters:', error)
      } finally {
        this.isLoading = false
      }
    },
    resetFilters: function() {
      this.filters = {
        statusIds: [],
        floorMin: null,
        floorMax: null,
        rooms: [],
      }
      // Apply empty filters to reset the apartment list
      this.applyFilters()
    },
    initializeStaticOptions: function() {
      // Initialize floors and rooms options from all apartments
      const apartments = this.$store.state.ApartmentStore.projectApartments || []
      if (apartments.length > 0) {
        // Initialize floors options
        const uniqueFloors = [
          ...new Set(
            apartments
              .map(apt => apt.floor)
              .filter(floor => floor !== null && floor !== undefined)
          ),
        ]
        this.staticFloorsOptions = uniqueFloors
          .sort((a, b) => a - b)
          .map(floor => ({
            text: floor.toString(),
            value: floor,
          }))

        // Initialize rooms options
        const uniqueRooms = [
          ...new Set(
            apartments
              .map(apt => apt.rooms)
              .filter(rooms => rooms !== null && rooms !== undefined)
          ),
        ]
        this.staticRoomsOptions = uniqueRooms
          .sort((a, b) => a - b)
          .map(rooms => ({
            text: rooms.toString(),
            value: rooms,
          }))
      }
    },
  },
  mounted: function() {
    // Initialize static dropdown options after component is mounted
    this.initializeStaticOptions()
  },
}
</script>
